/**
 * ReferencePersonForm Component
 *
 * Form for creating or editing a reference person (AI Avatar).
 * Features:
 * - Visual icon header with title and subtitle
 * - Name field (required) with character counter
 * - Description field (optional) with character counter
 * - Helpful hint about uploading images (create mode only)
 * - Proper primary/secondary button styling
 */

import { useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, UserPlus, Info } from 'lucide-react';
import type {
  IReferencePersonFormProps,
  IReferencePersonFormInput,
} from '@/@types/referencePersons';
import { REFERENCE_PERSON_CONSTANTS } from '@/@types/referencePersons';
import { cn } from '@/lib/utils';

function ReferencePersonForm({
  initialValues,
  onSubmit,
  onCancel,
  isSubmitting,
  mode,
}: IReferencePersonFormProps) {
  const [name, setName] = useState(initialValues?.name || '');
  const [description, setDescription] = useState(initialValues?.description || '');
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setError(null);

      // Validate name
      const trimmedName = name.trim();
      if (!trimmedName) {
        setError('Name is required');
        return;
      }
      if (trimmedName.length > REFERENCE_PERSON_CONSTANTS.MAX_NAME_LENGTH) {
        setError(`Name must be ${REFERENCE_PERSON_CONSTANTS.MAX_NAME_LENGTH} characters or less`);
        return;
      }

      // Validate description
      if (description.length > REFERENCE_PERSON_CONSTANTS.MAX_DESCRIPTION_LENGTH) {
        setError(
          `Description must be ${REFERENCE_PERSON_CONSTANTS.MAX_DESCRIPTION_LENGTH} characters or less`
        );
        return;
      }

      const formInput: IReferencePersonFormInput = {
        name: trimmedName,
        description: description.trim() || undefined,
      };

      await onSubmit(formInput);
    },
    [name, description, onSubmit]
  );

  const handleNameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
    setError(null);
  }, []);

  const handleDescriptionChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setDescription(e.target.value);
    setError(null);
  }, []);

  const isCreateMode = mode === 'create';

  return (
    <form onSubmit={handleSubmit} className="flex flex-col">
      {/* Header with Icon */}
      <div className="flex flex-col items-center gap-4 pb-6">
        <div className="w-16 h-16 rounded-full flex items-center justify-center bg-primary">
          <UserPlus className="w-7 h-7 text-primary-foreground" />
        </div>
        <div className="flex flex-col items-center gap-1 text-center">
          <h2 className="text-xl font-semibold text-foreground">
            {isCreateMode ? 'Create Reference Person' : 'Edit Reference Person'}
          </h2>
          <p className="text-sm text-muted-foreground">
            {isCreateMode
              ? 'Add a person for AI-consistent image generation'
              : 'Update person details'}
          </p>
        </div>
      </div>

      {/* Form Fields */}
      <div className="flex flex-col gap-5">
        {/* Name Field */}
        <div className="flex flex-col gap-1.5">
          <div className="flex items-center gap-1">
            <label htmlFor="person-name" className="text-sm font-medium text-foreground">
              Name
            </label>
            <span className="text-sm font-medium text-destructive">*</span>
          </div>
          <Input
            id="person-name"
            value={name}
            onChange={handleNameChange}
            placeholder="e.g., John Smith"
            maxLength={REFERENCE_PERSON_CONSTANTS.MAX_NAME_LENGTH}
            disabled={isSubmitting}
            className="h-11"
          />
          <p className="text-xs text-muted-foreground">
            {name.length}/{REFERENCE_PERSON_CONSTANTS.MAX_NAME_LENGTH} characters
          </p>
        </div>

        {/* Description Field */}
        <div className="flex flex-col gap-1.5">
          <div className="flex items-center gap-1">
            <label htmlFor="person-description" className="text-sm font-medium text-foreground">
              Description
            </label>
            <span className="text-xs text-muted-foreground">(optional)</span>
          </div>
          <Textarea
            id="person-description"
            value={description}
            onChange={handleDescriptionChange}
            placeholder="e.g., Company spokesperson, Marketing team lead"
            maxLength={REFERENCE_PERSON_CONSTANTS.MAX_DESCRIPTION_LENGTH}
            disabled={isSubmitting}
            rows={3}
            className="min-h-[88px] resize-none"
          />
          <p className="text-xs text-muted-foreground">
            {description.length}/{REFERENCE_PERSON_CONSTANTS.MAX_DESCRIPTION_LENGTH} characters
          </p>
        </div>

        {/* Hint Box - Only show in create mode */}
        {isCreateMode && (
          <div
            className={cn(
              'flex gap-2.5 p-3 rounded-lg',
              'bg-[#F0FDF4] light:bg-[#F0FDF4]',
              'dark:bg-[#1A2E1A]'
            )}
          >
            <Info className="w-4 h-4 flex-shrink-0 mt-0.5 text-[#16A34A] light:text-[#16A34A] dark:text-primary" />
            <p className="text-[13px] text-[#166534] light:text-[#166534] dark:text-primary">
              After creating, you'll be able to upload 3-5 reference photos for AI training.
            </p>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="p-3 text-sm rounded-lg bg-destructive/10 text-destructive">{error}</div>
        )}
      </div>

      {/* Divider */}
      <div className="h-px bg-border my-6 -mx-6" />

      {/* Actions */}
      <div className="flex justify-end gap-3 -mx-6 -mb-6 px-6 pb-6">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={isSubmitting}
          className="h-11 px-5"
        >
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={isSubmitting || !name.trim()}
          className="h-11 px-6 bg-primary text-primary-foreground hover:bg-primary/90"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              {isCreateMode ? 'Creating...' : 'Saving...'}
            </>
          ) : isCreateMode ? (
            'Create Person'
          ) : (
            'Save Changes'
          )}
        </Button>
      </div>
    </form>
  );
}

export { ReferencePersonForm };
